// Pure greeting composition for Charles' returning-user opener.
//
// Tone: warm, peer-level. Never "I'm seeing…", "detected…", or sentiment talk.
// Max 2 sentences before the chips.

export type GreetingInput = {
  firstName: string | null | undefined;
  openActionCount: number;
  topActionTitle: string | null | undefined;
  pulseAvg30d: number | null | undefined;
  pulseSampleCount: number;
};

export type GreetingOutput = {
  text: string;
  topActionTitleDisplay: string | null;
};

const MAX_TITLE_LEN = 60;
const ELLIPSIS = '\u2026'; // single Unicode ellipsis char

/**
 * Truncate to at most `max` display characters. If truncated, the last
 * character is a single Unicode ellipsis (U+2026), so total length stays
 * ≤ `max`. For max=60, the ellipsis sits at index 59 (the 60th char).
 */
export function truncateTitle(input: string, max: number = MAX_TITLE_LEN): string {
  const s = (input ?? '').trim();
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + ELLIPSIS;
}

function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm;
}

function nameGreeting(firstName: string | null | undefined): string {
  const n = (firstName ?? '').trim();
  return n ? `Hi ${n}.` : 'Hi there.';
}

function pulseClause(avg: number | null | undefined, samples: number): string | null {
  if (avg == null || samples < 3) return null;
  // Always render one decimal place (e.g. 8 -> "8.0", 7.4 -> "7.4").
  return `you're averaging ${avg.toFixed(1)} on your pulse`;
}

export function composeGreeting(input: GreetingInput): GreetingOutput {
  const { openActionCount, topActionTitle, pulseAvg30d, pulseSampleCount } = input;
  const hello = nameGreeting(input.firstName);
  const pulse = pulseClause(pulseAvg30d, pulseSampleCount);
  const safeCount = Math.max(0, Math.floor(openActionCount || 0));
  const topDisplay = topActionTitle ? truncateTitle(topActionTitle) : null;

  // Branch 1: zero open actions — "clear" sentence.
  if (safeCount === 0) {
    const actionClause = pulse
      ? `Your to-do list is clear and ${pulse}.`
      : 'Your to-do list is clear.';
    const followUp = pulse
      ? 'Want to log a fresh pulse, or pick something else?'
      : 'Want to log a fresh pulse, or pick something else?';
    return { text: `${hello} ${actionClause} ${followUp}`, topActionTitleDisplay: null };
  }

  // Branch 2: at least one open action.
  const thingWord = plural(safeCount, 'thing', 'things');
  const actionSentence = pulse
    ? `You have ${safeCount} ${thingWord} on your to-do list and ${pulse}.`
    : `You have ${safeCount} ${thingWord} on your to-do list.`;

  const followUp = topDisplay
    ? `Want to work through ${topDisplay}, or pick something else?`
    : 'Want to work through your top to-do, or pick something else?';

  return {
    text: `${hello} ${actionSentence} ${followUp}`,
    topActionTitleDisplay: topDisplay,
  };
}
